<template>
	<b-container fluid class='report-page'>
		<b-row>
			<b-col cols="2">
				<b-form-group>
					<label>{{ $t('report.start_date') }}</label>									
					<b-form-datepicker 
						v-model="startDate" 
						:locale="locale" 
						size="sm" 
						start-weekday="1" 
						v-bind="dateTranslations || {}"
						:date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric', weekday: 'short' }" >
					</b-form-datepicker>
					</b-form-group>
			</b-col>
			<b-col  cols="2">
				<b-form-group>
					<label>{{ $t('report.end_date') }}</label>									
					<b-form-datepicker 
						v-model="endDate" 
						:locale="locale"
						size="sm" 
						start-weekday="1" 
						v-bind="dateTranslations || {}"
						:date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric', weekday: 'short' }" >
					</b-form-datepicker>
				</b-form-group>
			</b-col>
			<b-col class="btn-v-align">
				<b-button type="submit" class="btn-success btn-report" @click="generateReport()" :disabled="buttonDisabled">
					<b-icon icon="arrow-clockwise" scale="1.2"></b-icon>
					{{ $t('report.generate') }}
				</b-button>
				<b-button class="btn-success btn-report" @click="printReport()" :disabled="buttonDisabled || !generated">
                    <b-icon icon="printer" scale="1.2"></b-icon>
                    {{ $t('report.print') }}
                </b-button>
				<b-button class="btn-success btn-report"  @click="generateXLS()" :disabled="buttonDisabled || !generated">
					<b-icon icon="download" scale="1.2"></b-icon>
					{{ $t('report.download') }}
				</b-button>
			</b-col>
            
		</b-row>
		<b-row>
			<b-col cols='9' class='report-col'>
				<b-table
					striped
					:items="fetchedItems"
					:fields="fields"
					:per-page="perPage"
					:current-page="currentPage"
					:busy="loading">
					<template #table-busy>
						<Loading />
					</template>

					<template v-slot:cell(actions)="row">
                        <b-button outline size="sm" @click="getDetails(row)" :disabled="row.item.blocksOnDrive == 0">{{ row.detailsShowing ? '-' : '+' }}</b-button>
                    </template>

					<template v-slot:row-details="row">
                        <div v-if="!row.item.mergedVehicle">
							<Loading />
						</div>
						<div class="subtable" v-else >	
							<div v-for="block of row.item.mergedVehicle" :key="block.id" class="subrow">
								<div class="detail_spacer"></div>
								<div class="subcol"> {{ block.withVehicle }} </div>
								<div class="subcol"> {{ block.withoutVehicle }} </div>
							</div>
						</div>
                    </template>

				</b-table>

					<TablePagination
						:total-rows="totalRows"
						:per-page="perPage"
						@pageChange="onPageChange">
					</TablePagination>
				</b-col>
				<b-col cols="3" > 
					<div class="summary">
						<card>
							<h6> {{ summary.period }} </h6>
							<b-row><b-col cols="8"> {{ $t('block_report.summary.total') }} </b-col><b-col> {{ summary.total }} </b-col></b-row>
							<b-row><b-col cols="8"> {{ $t('block_report.summary.ok') }} </b-col><b-col> {{ summary.ok }} </b-col></b-row>
							<b-row><b-col cols="8"> {{ $t('block_report.summary.error') }} </b-col><b-col> {{ summary.error }}</b-col></b-row>
						</card>
						<circle-chart-card 
							:percentage="this.okPercentage"
							:key="'ok_card'+this.okPercentage"  
							:description="$t('report.blocks_on_drive_with_vehicle')" 
							color="green" 
							:height="295">
						</circle-chart-card>
						<circle-chart-card 
							:percentage="this.errorPercentage"
							:key="'error_card'+this.errorPercentage"  
							:description="$t('report.blocks_without_vehicle')" 
							color="red" 
							:height="295">
						</circle-chart-card>
					</div>
			</b-col>
		</b-row>
	</b-container>
</template>

<script>
import TablePagination from '@/components/Misc/TablePagination';
import realtime from '@/store/realtime/realtime';
import moment from 'moment';
import CircleChartCard from 'src/components/UIComponents/Cards/CircleChartCard';

export default {
	components: { TablePagination, CircleChartCard },
	data: function() {
		return {
			perPage: 24,
			currentPage: 1,
            startDate: new moment().format('YYYY-MM-DD'),
			endDate: new moment().format('YYYY-MM-DD'),
			blocksData: [],
			summary: {
				period: "-",
				total: "-",
				ok: "-",
				error: "-"
			},
			fetchedItems: [],
			loading: false,
			generated: false,
            printing : false
		};
	},
	computed: {
		fields() {
            return [
                { key: 'date', label: this.$t('report.date') },
                { key: 'hour', label: this.$t('report.hour'),
                    formatter: function(value) {
						var hourStart = new moment(value, 'YYYY-MM-DD[T]HH:mm:ssZZ').format('HH');
						if (hourStart.startsWith('Invalid')) 
							return '';
						var hourEnd = +hourStart + 1;
						if (hourEnd == '00')
							hourEnd = '24';
						else if (hourEnd < 10)
							hourEnd = '0' + hourEnd;
						return hourStart + ' - ' + hourEnd;
					}
				},
                { key: 'blocksOnDrive', label: this.$t('report.blocks_on_drive') },
                { key: 'blocksWithVehicle', label: this.$t('report.blocks_on_drive_with_vehicle') },
                { key: 'blocksWithoutVehicle', label: this.$t('report.blocks_without_vehicle') },
				{ key: 'actions', label: '' }               
            ];
        },
		fetchedItemsList() {
			return this.fetchedItems;
		},
		subtable_fields(){
			return [
				{ key: 'withVehicle', label: this.$t('report.trips_on_drive_with_vehicle') },
                { key: 'withoutVehicle', label: this.$t('report.trips_without_vehicle') }
			];
		},
		items() {
			return this.$store.state.realtime.blocksReport.list;
		},
		totalRows() {
			return this.filteredRows ? this.filteredRows : (this.items ? this.items.length : 0);
		},
        locale: function() {
            return this.$t('datepicker.locale');
        },
        dateTranslations: function() {
            return this.$t('datepicker');
        },
       
		okPercentage(){
			var percentage = 0;
			if(this.summary.total != "-" && this.summary.ok != "-" && this.summary.ok > 0)
				percentage = Math.round((this.summary.ok / this.summary.total) * 100);

			return percentage;
		},
		errorPercentage(){
			var percentage = 0;
			if(this.summary.total != "-" && this.summary.error != "-" && this.summary.error > 0)
				percentage = Math.round((this.summary.error / this.summary.total) * 100);

			return percentage;
		},
		buttonDisabled() {
			return !this.startDate || !this.endDate 
					|| (moment(this.endDate, "YYYY-MM-DD") > moment(this.startDate, "YYYY-MM-DD").add(7, 'days') || this.startDate > this.endDate)
					|| this.loading || this.printing;
		}	
	},
	created() {
        if (!this.$store.state.realtime) {
            this.$store.registerModule("realtime", realtime);
		}
		
		window.addEventListener("afterprint", this.afterprint);
	},
	beforeDestroy(){
        window.removeEventListener("afterprint", this.afterprint);
    },
	methods: {
        generateReport() {
		
			this.loading = true;
			this.resetSummary();

            var timeRange = {
                'startDate': this.startDate,
                'endDate': this.endDate
            }
			var blocksWithVehicle = 0;
			var blocksWithoutVehicle = 0;
			var blocksOnDrive = 0;

			this.$store.dispatch('realtime/getBlocksReport', timeRange).then(response => {
				this.fetchedItems = response.data;

				for (var a of response.data) {
					blocksWithVehicle += a.cumulativeBlocksWithVehicle;
					blocksWithoutVehicle += a.cumulativeBlocksWithoutVehicle;
				}
			})
			.finally(() => { 
				this.loading = false;
				this.generated = true;

				var startTime = new moment(this.startDate, "YYYY-MM-DD");
				var endTime2 = new moment(this.endDate, 'YYYY-MM-DD');
				this.summary.period = startTime.format("DD.MM.YYYY") + " - " + endTime2.format("DD.MM.YYYY");
				blocksOnDrive = blocksWithVehicle + blocksWithoutVehicle;

				if (blocksOnDrive > 0) {
					// this.summary.total = blocksOnDrive;
					this.summary.total = blocksWithVehicle + blocksWithoutVehicle;
					this.summary.ok = blocksWithVehicle;
					this.summary.error = blocksWithoutVehicle;
				}
        });
		},
        generateXLS() {
            this.loading = true;

            var params = {
                'startDate': this.startDate,
                'endDate': this.endDate,
                'header1': this.$t('report.date'),
                'header2': this.$t('report.hour'),
                //'header3': this.$t('report.blocks_on_drive'),
                //'header4': this.$t('report.blocks_on_drive_with_vehicle'),
                //'header5': this.$t('report.blocks_without_vehicle')
                'header3': this.$t('report.block'),
                'header4': this.$t('report.vehicle_assigned')
            };
            this.$store.dispatch('realtime/getBlocksReportXLS', params)
                .finally(() => { this.loading = false; });
        },
		getDetails(row){
			row.toggleDetails();

			if(row.detailsShowing) return;
			let item = row.item;

            var startTime = new moment(item.hour, 'YYYY-MM-DD[T]HH:mm');
            var timeRange = {
                'startTime': startTime.format('YYYY-MM-DD[T]HH:mm:ss'),
                'endTime': startTime.format('YYYY-MM-DD[T]HH:[59:59]')
			}

			this.$store.dispatch('realtime/getBlocks' , timeRange).then( response => {

				let withVehicle = [];
				let withoutVehicle = [];
				let mergedList = [];

				for( let block of response.data){
					// if(block.vehicleLabel != ""){
					if(block.vehicleId && block.vehicleId != ""){
						if(!withVehicle.some( item => item.id == block.id))  withVehicle.push(block);
					}else{
						if(!withoutVehicle.some( item => item.id == block.id))  withoutVehicle.push(block);
					}
				}

				withVehicle.sort( (a,b) => (a.blockLabel > b.blockLabel) ? 1 : ((b.blockLabel > a.blockLabel) ? -1 : 0) );
				withoutVehicle.sort( (a,b) => (a.blockLabel > b.blockLabel) ? 1 : ((b.blockLabel > a.blockLabel) ? -1 : 0) );

				for(let block of withVehicle){
					let mergedBlocks = {};
					mergedBlocks.id = mergedList.length;
					mergedBlocks.withVehicle = block.blockLabel;
					mergedBlocks.withoutVehicle = "";
					mergedList.push(mergedBlocks)
				}

				for(let [index, block] of withoutVehicle.entries()){
					if(mergedList[index]){
						mergedList[index].withoutVehicle = block.blockLabel;
					}
					else{
						let mergedBlock = {};
						mergedBlock.id = mergedList.length;
						mergedBlock.withVehicle = "";
						mergedBlock.withoutVehicle = block.blockLabel;
						mergedList.push(mergedBlock)
					}
				}

				let index = this.fetchedItems.findIndex( fetchedItem => fetchedItem.hour == item.hour );
				let newObject = this.fetchedItems[index];
				newObject.mergedVehicle = mergedList;
				this.$set(this.fetchedItems, index, newObject)

				//this.fetchedItems.filter( fetchedItem => fetchedItem.hour == item.hour)[0].mergedVehicle = mergedList;
            });
		},
		onFiltered(filteredItems) {
			this.filteredRows = filteredItems.length;
			this.currentPage = 1;
		},
		onPageChange(page) {
			this.currentPage = page;
		},
		resetSummary(){
			this.summary.period = "-";
			this.summary.total = "-";
			this.summary.ok = "-";
			this.summary.error = "-";
		},
		printReport(){
            this.printing = true;
            this.perPage = this.items.length
            // timeout needed because table takes some time to render
            setTimeout(() => { window.print(); }, 10);   
        },
        afterprint(){
            this.perPage = 24;
            this.printing = false;
        }
	}
};
</script>

<style scoped lang="scss">

.subtable {
  
	.header-row{
		padding-right:4%;
		padding-left:3%;
		display: flex;
		flex-direction: row;
	}
	
	.subrow {
		padding-right:4%;
		padding-left:3%;
		display: flex;
		flex-direction: row;
		font-size: 13px;
		font-weight: bold;
	}

	.subcol {
		flex: 1;
	}
	.detail_spacer {
		width: 43%;
  }
}

</style>